// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Target, BookOpen, Users, Shield, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ChiSiamoPage = () => {
  const [counters, setCounters] = useState(null);
  const [storia, setStoria] = useState(null);
  const [storiaError, setStoriaError] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/counters`).then(r => r.ok ? r.json() : null).then(setCounters).catch(() => {});
    // P3 — La nostra storia da DB
    fetch(`${API_URL}/api/contenuti/chi_siamo_storia`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => setStoria(d?.testo || null))
      .catch(() => setStoriaError(true));
  }, []);

  const valori = [
    { icon: <BookOpen className="w-5 h-5" />, title: 'Educazione prima di tutto', desc: 'Non vendiamo prodotti finanziari. Non riceviamo commissioni da banche o assicurazioni. Il nostro unico obiettivo è che tu capisca davvero come funziona il denaro.' },
    { icon: <Target className="w-5 h-5" />, title: 'Praticità, non teoria', desc: 'La finanza personale si impara facendo. Per questo ogni concetto è accompagnato da un calcolatore, un esempio numerico, o una simulazione concreta.' },
    { icon: <Users className="w-5 h-5" />, title: 'Accessibilità per tutti', desc: 'I livelli Base e Intermedio sono gratuiti per sempre. Crediamo che l\'educazione finanziaria di base debba essere un diritto, non un privilegio.' },
    { icon: <Shield className="w-5 h-5" />, title: 'Trasparenza e disclaimer', desc: 'SAPIENS è uno strumento educativo. Non siamo consulenti finanziari. Ogni calcolatore e articolo include un disclaimer chiaro. Le decisioni sono sempre tue.' },
  ];

  return (
    <div className="min-h-screen bg-[#1C1C1A]">
      <Helmet>
        <title>Chi Siamo — SAPIENS Educazione Finanziaria</title>
        <meta name="description" content="SAPIENS è la piattaforma italiana di educazione finanziaria pratica. Scopri la nostra missione, i valori e perché abbiamo creato questo progetto." />
        <link rel="canonical" href="https://finsapiens.it/chi-siamo" />
      </Helmet>

      <div className="container-wide py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
          <div className="text-sm font-medium text-[#C9A84C] uppercase tracking-wider mb-4">Chi siamo</div>
          <h1 className="text-4xl sm:text-5xl font-semibold text-[#E8E8E4] leading-tight mb-6">
            La finanza personale<br />
            <span style={{ color: '#C9A84C' }}>dovrebbe essere semplice.</span>
          </h1>
          <p className="text-xl text-[#888780] leading-relaxed">
            SAPIENS è una piattaforma italiana di educazione finanziaria. Non un'app bancaria, non un broker, non un comparatore. Solo educazione: lezioni, calcolatori, e guide scritte per chi parte da zero.
          </p>
        </motion.div>
      </div>

      <div className="border-y border-white/10 py-16">
        <div className="container-wide">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
              <h2 className="text-2xl font-semibold text-[#E8E8E4] mb-5">Il problema che vogliamo risolvere</h2>
              <div className="space-y-4 text-[#888780] leading-relaxed">
                <p>Gli italiani risparmiano molto, ma investono poco. Una parte enorme della ricchezza delle famiglie è ferma sui conti correnti, a perdere valore con l'inflazione ogni anno.</p>
                <p>Non è pigrizia. È mancanza di strumenti semplici per capire le alternative. L'industria finanziaria usa un linguaggio volutamente complicato. Il sistema scolastico non insegna finanza personale.</p>
                <p><strong className="text-[#E8E8E4]">SAPIENS vuole cambiare questo.</strong> Un percorso strutturato, dall'ABC del denaro fino alla pianificazione previdenziale. Gratuito nella parte fondamentale.</p>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
              {/* Deployment 17 — P0/P3: contatori dinamici da /api/counters */}
              <div className="grid grid-cols-2 gap-4">
                {[
                  { num: counters?.calculators ?? '—', label: 'Calcolatori gratuiti', testid: 'chi-siamo-calculators' },
                  { num: counters?.lessons ?? '—', label: 'Lezioni strutturate', testid: 'chi-siamo-lessons' },
                  { num: counters?.glossary ?? '—', label: 'Termini nel glossario', testid: 'chi-siamo-glossary' },
                  { num: '4', label: 'Livelli di competenza', testid: 'chi-siamo-livelli' },
                ].map(s => (
                  <div key={s.label} className="sapiens-card p-5 text-center" data-testid={s.testid}>
                    <div className="text-3xl font-bold text-[#C9A84C] mb-1">{s.num}</div>
                    <div className="text-sm text-[#888780]">{s.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <div className="container-wide py-16">
        <h2 className="text-2xl font-semibold text-[#E8E8E4] mb-8">I nostri valori</h2>
        <div className="grid sm:grid-cols-2 gap-5">
          {valori.map((v, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }} className="sapiens-card p-6">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4 text-[#C9A84C]" style={{ background: 'rgba(201,168,76,0.1)' }}>
                {v.icon}
              </div>
              <h3 className="font-semibold text-[#E8E8E4] mb-2">{v.title}</h3>
              <p className="text-sm text-[#888780] leading-relaxed">{v.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* La nostra storia — Deployment 17 P3: testo da /api/contenuti/chi_siamo_storia */}
      <div className="border-t border-white/10 py-16">
        <div className="container-wide max-w-2xl">
          <h2 className="text-2xl font-semibold text-[#E8E8E4] mb-8">La nostra storia</h2>
          <div data-testid="chi-siamo-storia">
            {!storia && !storiaError && (
              <p className="text-sm text-[#888780]">Caricamento...</p>
            )}
            {storiaError && (
              <p className="text-sm text-[#888780]">Contenuto temporaneamente non disponibile.</p>
            )}
            {storia && (
              <div className="text-[#888780] leading-relaxed whitespace-pre-line text-sm">
                {storia}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-[#252523] border-t border-white/10 py-12">
        <div className="container-wide max-w-2xl text-center">
          <h3 className="font-semibold text-[#E8E8E4] mb-3">Una nota importante</h3>
          <p className="text-sm text-[#888780] leading-relaxed mb-4">
            SAPIENS è uno strumento di educazione finanziaria e non costituisce consulenza finanziaria, fiscale o legale. I contenuti sono a scopo puramente didattico. Prima di prendere qualsiasi decisione finanziaria significativa, ti invitiamo a consultare un professionista abilitato.
          </p>
          <Link to="/disclaimer" className="inline-flex items-center gap-1.5 text-sm text-[#C9A84C] hover:underline">
            Leggi il Disclaimer completo <ExternalLink className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ChiSiamoPage;
